import { useState, useEffect } from "react";
/**
 * Queue Track Row Component
 *
 * Displays an individual track in the playback queue with controls to play or remove it.
 * Shows track metadata including artist, title, and remix information in a compact row format.
 */
// Import necessary Redux actions
import {
  addToCrate,
  checkDownloadEligibility,
  decreaseDownloadCount,
  getSignedUrl,
  increaseDownloadCount,
  removeFromCrate,
} from "../../store";
import store from "../../store";

// Import custom hooks
import { useThunk } from "../../hooks/useThunk";
import { useLoggedUser } from "../../contexts/LoggedUserContext";
import { useLanguage } from "../../contexts/LanguageContext";

// Import utility functions
import showToast from "../../utils/toastNotifications";
import tryTrackDownload from "../../utils/trackDownloader";
import { downloadQueue } from "../../utils/downloadQueue";

/* Supreme Edits player context */
import { useSupremeEditsPlayer } from "../../contexts/SupremeEditsContext";

/* Components */
import PlayButton from "./PlayButton";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";

/**
 * VersionTrackRow Component
 *
 * @param {Object} trackMetadata - Core track information (title, artist)
 * @param {Object} remixMetadata - Additional data for remixes, edits and featured artists
 * @param {Array} playlistTracks - Collection of tracks in the current playlist
 * @param {Number} playIndex - Index position of this track in the queue
 * @returns {JSX.Element} Queue track row component
 */
const VersionTrackRow = ({
  trackMetadata,
  remixMetadata,
  playIndex,
  version,
}) => {
  // State to control download button visibility
  const [disableDownloadButton, setDisableDownloadButton] = useState(false);
  // State to manage border highlight visibility
  const [showBorder, setShowBorder] = useState(false);

  // Access player context for queue management
  const { currentTrack, versionIndex, editIndex, supremeCycle, currentIndex } =
    useSupremeEditsPlayer();

  // Access logged user context for download management
  const {
    isBlockedDownload,
    refreshUserDownloads,
    downloadedVersions,
    optimisticIncrementDownload,
    optimisticDecrementDownload,
  } = useLoggedUser();
  const { translations } = useLanguage();

  const [doIncreaseDownloadCount] = useThunk(increaseDownloadCount);
  const [doCheckDownloadEligibility, isChecking] = useThunk(
    checkDownloadEligibility,
  );
  const [doDecreaseDownloadCount] = useThunk(decreaseDownloadCount);
  const [doGetSignedUrl, isGettingSignedUrl, errorGettingSignedUrl] =
    useThunk(getSignedUrl);

  useEffect(() => {
    if (remixMetadata?.versions) {
      let shouldDisable = true;
      remixMetadata.versions.forEach((v) => {
        if (
          (downloadedVersions &&
            downloadedVersions[v.id] &&
            downloadedVersions[v.id] < 2) ||
          !downloadedVersions[v.id]
        ) {
          shouldDisable = false;
        }
      });
      setDisableDownloadButton(shouldDisable);
    }
  }, [remixMetadata, downloadedVersions]);

  // Updates border visibility based on current playing version
  useEffect(() => {
    setShowBorder(
      supremeCycle[currentIndex]?.trackId === trackMetadata?.trackId &&
        supremeCycle[currentIndex]?.editInfos[editIndex]?.id ===
          remixMetadata?.id &&
        supremeCycle[currentIndex]?.editInfos[editIndex]?.versions[versionIndex]
          ?.versionType === version?.versionType,
    );
  }, [
    supremeCycle,
    currentIndex,
    trackMetadata,
    remixMetadata,
    editIndex,
    versionIndex,
    version,
  ]);

  const dispatch = useDispatch();
  const crateItems = useSelector((state) => state.crate.items);

  const handleDownload = async (audioFile, versionType, versionId) => {
    const trackId = trackMetadata?._id;
    const trackTitle = trackMetadata?.trackTitle;
    const editId = remixMetadata?.id;
    const editTitle = remixMetadata?.editTitle;
    const remixerId = remixMetadata?.remixerId;

    const downloadId = `${trackId}_${editId}_${versionId}_${Date.now()}`;
    const downloadUrl = audioFile
      .replace("/play", "")
      .split("tracks")
      .join("tracks/download");

    // Add to queue
    const queuedId = downloadQueue.add(
      downloadId,
      // The download function that will execute when it's this item's turn
      async () => {
        const maxRetries = 3;
        let attempt = 0;

        while (attempt < maxRetries) {
          try {
            // Run signed URL generation and eligibility check in parallel with a unified 20s timeout
            const [signedUrlData, eligibilityResult] = await Promise.race([
              Promise.all([
                doGetSignedUrl({ path: downloadUrl }),
                doCheckDownloadEligibility({
                  trackId: trackId,
                  editId: editId,
                  remixerId: remixerId ? remixerId : null,
                  versionId: versionId,
                })
              ]),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error("Verification timed out")), 20000)
              )
            ]);

            if (!signedUrlData?.response?.data?.url) {
              throw new Error("Invalid signed URL response");
            }

            const signedUrl = signedUrlData.response.data.url;

            // Execute download
            await tryTrackDownload(
              signedUrl,
              versionType,
              versionId,
              isBlockedDownload,
              refreshUserDownloads,
              showToast,
              trackId,
              trackTitle,
              editId,
              editTitle,
              remixerId,
              () => eligibilityResult,
              doIncreaseDownloadCount,
              doDecreaseDownloadCount,
              translations?.downloadToast?.limitReached,
              () => {},
              optimisticIncrementDownload,
              optimisticDecrementDownload,
            );

            // showToast("success", `${versionType} downloaded successfully!`);
            return true;
          } catch (error) {
            attempt++;

            if (attempt >= maxRetries) {
              // Final failure after all retries
              console.error("Download failed after all retries:", error);
              throw error;
            }

            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 4000);
            await new Promise((resolve) => setTimeout(resolve, delay));
            console.log(`Retrying download (${attempt}/${maxRetries})...`);
          }
        }
      },
      // Metadata
      {
        versionType,
        versionId,
        trackTitle,
        editTitle,
        trackId,
        editId,
        downloadedCount: downloadedVersions[versionId] || 0,
      },
    );

    if (queuedId) {
      optimisticIncrementDownload(versionId);
    }

    // showToast("info", `${versionType} added to download queue`);
  };

  const handleAddToCrate = () => {
    if (disableDownloadButton) {
      return;
    }

    const currentCrateItems = store.getState().crate.items;
    if (currentCrateItems.length >= 12) {
      showToast(
        "warning",
        "Cannot add more tracks. Maximum crate capacity (12 tracks) reached.",
      );
      return;
    }

    try {
      dispatch(
        addToCrate({
          id: `${trackMetadata?._id}_${remixMetadata?.id}_${version?.id}`,
          trackId: trackMetadata?._id,
          editId: remixMetadata?.id,
          trackTitle: trackMetadata?.trackTitle,
          editTitle: remixMetadata?.editTitle,
          artists: trackMetadata?.artists,
          version: version,
          remixerId: remixMetadata?.remixerId,
          featuredArtists: remixMetadata?.featuredArtists,
          additionalMainArtists: remixMetadata?.additionalMainArtists,
        }),
      );
    } catch (error) {
      showToast(
        "error",
        `Failed to add ${trackMetadata?.trackTitle}${
          remixMetadata?.editTitle
            ? " " + "(" + remixMetadata?.editTitle + ")"
            : ""
        } ${" - " + version.versionType} to crate!`,
      );
    }
    showToast(
      "success",
      `${trackMetadata?.trackTitle}${
        remixMetadata?.editTitle
          ? " " + "(" + remixMetadata?.editTitle + ")"
          : ""
      } ${" - " + version.versionType} added to crate successfully!`,
    );
  };

  const handleRemoveFromCrate = () => {
    dispatch(
      removeFromCrate(
        `${trackMetadata?._id}_${remixMetadata?.id}_${version?.id}`,
      ),
    );
    showToast(
      "success",
      `${trackMetadata?.trackTitle}${
        remixMetadata?.editTitle
          ? " " + "(" + remixMetadata?.editTitle + ")"
          : ""
      } ${" - " + version.versionType} removed from crate successfully!`,
    );
  };

  return (
    <article className="relative group/version-row w-full h-fit flex flex-col justify-center items-center rounded-lg bg-transparent transition-all duration-500 ease-in-out animate-opacity-scale">
      <div className="relative flex flex-col w-full h-full justify-center items-center p-[1px]">
        {/* Wrapper for border highlight */}
        <div
          className={`${
            showBorder
              ? "border-gradient opacity-100"
              : "bg-transparent p-[1px] opacity-0"
          } transition-opacity duration-500 animate-opacity-scale absolute top-0 max-h-[60px] sm:max-h-[70px] lg:max-h-[80px] w-full h-full z-0`}
        />

        {/* Version Row Container */}
        <div
          className={`relative z-10 flex flex-row w-full h-[45px] sm:h-[50px] lg:h-[60px] px-[6px] sm:px-[7px] lg:px-[8px] py-[3px] sm:py-[3.5px] lg:py-[4px] content-center justify-center items-center gap-4 sm:gap-5 lg:gap-6 rounded-lg ${
            showBorder ? "bg-[#121517]" : ""
          }`}
        >
          {/* Play control area */}
          <div className="group w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 relative cursor-pointer">
            <PlayButton
              className="absolute opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 group-hover:opacity-100 transition-all duration-500 cursor-pointer"
              metaData={trackMetadata}
              remixMetadata={remixMetadata}
              version={version}
              thisVersionIndex={playIndex}
              thisPlaylistName="version"
              type="version"
              explicitVersionIndex={playIndex}
            />
          </div>

          {/* Track metadata display area */}
          <div className="playlist-info flex flex-1 flex-col gap-0 justify-start content-center items-start overflow-hidden max-w-full w-[min(200px,100%)]">
            {/* Artist and featured artist names with proper formatting */}
            {currentTrack?.trackId === trackMetadata?.trackId &&
              currentTrack?.editInfos?.[editIndex]?.versions?.[versionIndex]
                ?.versionType === version?.versionType && (
                <p className="animate-opacity-scale text-gradient text-[10px] sm:text-[11px] lg:text-xs whitespace-nowrap max-w-full truncate overflow-hidden uppercase">
                  Now playing
                </p>
              )}

            {/* Track title with optional edit title */}
            <p className="text-secondary text-xs sm:text-sm lg:text-sm font-bold whitespace-nowrap overflow-hidden">
              {version?.versionType}
            </p>
          </div>

          {/* Download button with versions dropdown */}
          <div
            className={`w-5 h-5 p-[2px] cursor-pointer hidden md:block ${
              disableDownloadButton ? "opacity-30" : "opacity-100"
            } transition-opacity duration-500`}
            onClick={() =>
              !disableDownloadButton &&
              handleDownload(
                version?.audioFile,
                version?.versionType,
                version?.id,
              )
            }
          >
            <svg
              className="w-full h-full"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.99935 8.98067L3.05018 6.03234L3.64018 5.43234L5.58268 7.37484V0.166504H6.41602V7.37484L8.35768 5.43317L8.94852 6.03234L5.99935 8.98067ZM1.51268 11.8332C1.12879 11.8332 0.808516 11.7048 0.551849 11.4482C0.295182 11.1915 0.166571 10.871 0.166016 10.4865V8.46734H0.999349V10.4865C0.999349 10.6148 1.05268 10.7326 1.15935 10.8398C1.26602 10.9471 1.38352 11.0004 1.51185 10.9998H10.4869C10.6146 10.9998 10.7321 10.9465 10.8394 10.8398C10.9466 10.7332 10.9999 10.6154 10.9994 10.4865V8.46734H11.8327V10.4865C11.8327 10.8704 11.7044 11.1907 11.4477 11.4473C11.191 11.704 10.8705 11.8326 10.486 11.8332H1.51268Z"
                fill="#8D8D8D"
              />
            </svg>
          </div>

          {/* Options menu toggle with dropdown */}
          {crateItems.some(
            (item) =>
              item.id ===
              `${trackMetadata?._id}_${remixMetadata?.id}_${version?.id}`,
          ) ? (
            <Tooltip
              title="Remove from crate"
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    color: "#8D8D8D",
                    backgroundColor: "#1A1924",
                  },
                },
                arrow: {
                  sx: {
                    color: "#1A1924",
                  },
                },
              }}
              arrow
            >
              <div
                className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5 p-[1px] cursor-pointer"
                onClick={handleRemoveFromCrate}
              >
                <svg
                  className="w-full h-full"
                  viewBox="0 0 16 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.09589 7.95231H9.90541M8.00065 9.91041V6.04755M1.33398 2.23803V10.8095C1.33398 11.3146 1.53466 11.7991 1.89188 12.1563C2.24909 12.5135 2.73357 12.7142 3.23875 12.7142H12.7626C13.2677 12.7142 13.7522 12.5135 14.1094 12.1563C14.4666 11.7991 14.6673 11.3146 14.6673 10.8095V5.09231C14.6671 4.5873 14.4663 4.10306 14.1091 3.74606C13.7519 3.38905 13.2676 3.1885 12.7626 3.1885L8.00065 3.19041L6.09589 1.28564H2.28637C2.03378 1.28564 1.79154 1.38598 1.61293 1.56459C1.43432 1.7432 1.33398 1.98544 1.33398 2.23803Z"
                    stroke="#3A3A3A"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </Tooltip>
          ) : (
            <Tooltip
              title={`${
                disableDownloadButton ? "Max downloads reached" : "Add to crate"
              }`}
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    color: "#8D8D8D",
                    backgroundColor: "#1A1924",
                  },
                },
                arrow: {
                  sx: {
                    color: "#1A1924",
                  },
                },
              }}
              arrow
            >
              <div
                className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5 p-[1px] cursor-pointer"
                onClick={handleAddToCrate}
              >
                <svg
                  className="w-full h-full"
                  viewBox="0 0 16 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.09589 7.95231H9.90541M8.00065 9.91041V6.04755M1.33398 2.23803V10.8095C1.33398 11.3146 1.53466 11.7991 1.89188 12.1563C2.24909 12.5135 2.73357 12.7142 3.23875 12.7142H12.7626C13.2677 12.7142 13.7522 12.5135 14.1094 12.1563C14.4666 11.7991 14.6673 11.3146 14.6673 10.8095V5.09231C14.6671 4.5873 14.4663 4.10306 14.1091 3.74606C13.7519 3.38905 13.2676 3.1885 12.7626 3.1885L8.00065 3.19041L6.09589 1.28564H2.28637C2.03378 1.28564 1.79154 1.38598 1.61293 1.56459C1.43432 1.7432 1.33398 1.98544 1.33398 2.23803Z"
                    stroke="#8D8D8D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </article>
  );
};

export default VersionTrackRow;
